import { useEffect, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import InstallAppCoach from '../../shared/src/components/InstallAppCoach';
import IdentifyButton from '../../shared/src/components/IdentifyButton';
import GuestNav from '../../shared/src/components/GuestNav';
import SiteHeader from '../../shared/src/components/SiteHeader';
import GuestPageFrame from '../../shared/src/components/GuestPageFrame';
import { getHomeHref } from '../../shared/src/utils/routing';
import { applyDefaultLanguage, applyLocaleOverrides, readLocaleOverrides } from '../../shared/src/i18n/init';
import HomePage from './pages/HomePage';
import IdentifyPage from './pages/IdentifyPage';
import NotFoundPage from './pages/NotFoundPage';
import { fetchSitePhotos } from '../../shared/src/utils/sitePhotos';
import { syncSiteChrome } from '../../shared/src/utils/siteChrome';
import { isVisitorMode, needsPartyPerson, personalGuestIcon, personalGuestName, type GuestIdentity, type SitePhotoMap } from '@wedding/shared';

const apiBase = '/api';
const BOOT_CAP_MS = 4000;
const IMAGE_WAIT_MS = 2500;
const CRITICAL_PHOTO_SLOTS = ['home_logo', 'home_hero', 'home_bg'] as const;

interface SettingsRow {
  key: string;
  value: string;
}

interface HomeBootstrap {
  settings: Record<string, string>;
  photos: SitePhotoMap;
}

let homeBootstrap: Promise<HomeBootstrap> | null = null;

function dismissBootScreen() {
  document.getElementById('boot-screen')?.remove();
}

function preloadImage(url: string): Promise<void> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = url;
  });
}

function loadHomeBootstrap(): Promise<HomeBootstrap> {
  if (!homeBootstrap) {
    homeBootstrap = (async () => {
      const photosPromise = fetchSitePhotos(__BASE_PATH__);
      try {
        await fetch(`${apiBase}/auth/session`, { method: 'POST', credentials: 'include' });
      } catch { /* session cookie is still required before the reads below */ }

      const [settings, photos] = await Promise.all([
        fetch(`${apiBase}/settings`, { credentials: 'include' })
          .then(async (r) => {
            if (!r.ok) return {} as Record<string, string>;
            const j = await r.json() as { data?: SettingsRow[] };
            const sMap: Record<string, string> = {};
            for (const s of j.data ?? []) sMap[s.key] = s.value;
            return sMap;
          })
          .catch(() => ({}) as Record<string, string>),
        photosPromise,
      ]);

      return { settings, photos };
    })().catch((err) => {
      homeBootstrap = null;
      throw err;
    });
  }
  return homeBootstrap;
}

export default function App() {
  const location = useLocation();
  const { i18n } = useTranslation();
  const [siteSettings, setSiteSettings] = useState<Record<string, string>>({});
  const [sitePhotos, setSitePhotos] = useState<SitePhotoMap>({});
  const [sessionReady, setSessionReady] = useState(false);
  const [bootstrapDone, setBootstrapDone] = useState(false);
  const [guest, setGuest] = useState<GuestIdentity | null>(null);
  const [guestChecked, setGuestChecked] = useState(false);
  const [guestPath, setGuestPath] = useState(location.pathname);
  if (sessionReady && guestPath !== location.pathname) {
    setGuestPath(location.pathname);
    setGuest(null);
    setGuestChecked(false);
  }

  useEffect(() => {
    const cap = window.setTimeout(dismissBootScreen, BOOT_CAP_MS);
    let cancelled = false;

    loadHomeBootstrap()
      .then(({ settings, photos }) => {
        if (cancelled) return;
        applyDefaultLanguage(settings['default_language'] ?? 'en_hk');
        applyLocaleOverrides(readLocaleOverrides(settings));
        setSiteSettings(settings);
        setSitePhotos(photos);
        setSessionReady(true);
        setBootstrapDone(true);
      })
      .catch(() => {
        if (cancelled) return;
        setSessionReady(true);
        setBootstrapDone(true);
      });

    return () => {
      cancelled = true;
      window.clearTimeout(cap);
    };
  }, []);

  useEffect(() => {
    if (!sessionReady) return;
    let cancelled = false;
    const check = async () => {
      try {
        const res = await fetch(`${apiBase}/guests/me`, { credentials: 'include' });
        if (!cancelled) {
          if (res.ok) {
            const json = await res.json();
            setGuest(json.data);
          } else {
            setGuest(null);
          }
        }
      } catch {
        if (!cancelled) setGuest(null);
      }
      if (!cancelled) setGuestChecked(true);
    };
    check();
    return () => { cancelled = true; };
  }, [sessionReady, location.pathname]);

  useEffect(() => {
    if (!bootstrapDone) return;
    let cancelled = false;

    const urls = CRITICAL_PHOTO_SLOTS
      .map((slot) => sitePhotos[slot])
      .filter((url): url is string => Boolean(url));

    const reveal = async () => {
      if (urls.length > 0) {
        await Promise.race([
          Promise.all(urls.map(preloadImage)),
          new Promise<void>((resolve) => { window.setTimeout(resolve, IMAGE_WAIT_MS); }),
        ]);
      }
      if (!cancelled) dismissBootScreen();
    };

    void reveal();
    return () => { cancelled = true; };
  }, [bootstrapDone, sitePhotos]);

  useEffect(() => {
    syncSiteChrome({
      title: siteSettings['website_meta_title'] || siteSettings['site_title_home'] || 'Wedding',
      description: siteSettings['website_meta_description'] || 'Wedding guest experience',
      image: siteSettings['website_meta_image'] || undefined,
      iconHref: siteSettings['website_icon'] || '/icon-192.png',
      gaTagScript: siteSettings['ga_tag_script'] || undefined,
    });
  }, [siteSettings]);

  const visitorMode = isVisitorMode(siteSettings);
  const switchName = guestChecked && guest && !needsPartyPerson(guest) && location.pathname !== '/identify'
    ? personalGuestName(guest, i18n.language)
    : null;

  return (
    <GuestPageFrame>
      <IdentifyButton
        guestName={switchName}
        icon={switchName && guest ? personalGuestIcon(guest) : null}
        visitor={visitorMode && guestChecked && !guest && location.pathname !== '/identify'}
        homeHref={getHomeHref()}
      />
      <GuestNav />
      <SiteHeader
        coupleNames={siteSettings['couple_names'] ?? ''}
        coupleNamesZh={siteSettings['couple_names_zh'] ?? ''}
        homeHref={getHomeHref()}
        logoUrl={sitePhotos['home_logo']}
      />
      <InstallAppCoach appId="home" appName="Wedding App" enabled={location.pathname === '/'} priority="high" />
      <Routes>
        <Route
          path="/"
          element={
            <HomePage
              guest={guest}
              guestChecked={guestChecked}
              visitorMode={visitorMode}
              weddingDate={siteSettings['wedding_date'] || null}
              timezone={siteSettings['app_timezone']}
              sitePhotos={sitePhotos}
            />
          }
        />
        <Route path="/identify" element={<IdentifyPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </GuestPageFrame>
  );
}
